import React from 'react'

import styled from '@emotion/styled'
import { Box } from '@gousto-internal/citrus-react'

import { FlexPortionFooter as SharedFlexPortionFooter } from '@features/recipe-tile'
import type { Recipe } from '@library/menu-service'

import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { AddRecipeButton } from 'routes/Menu/components/Detail/AddRecipeButton'
import { useBasket } from 'routes/Menu/domains/basket'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'

const ModalFooterContent = styled(Box)({
  margin: '-16px',
  width: 'calc(100% + 32px)',

  '@media (min-width: 640px)': {
    margin: '-24px',
    width: 'calc(100% + 48px)',
  },
})

type FlexPortionFooterProps = {
  id: RecipeUUID
  position?: string
  surchargeLabel?: string | null
  isNegativeSurcharge?: boolean
  isRecipeAvailable?: boolean
  onRecipeDetailsClose?: () => void
  numPortions: number
  portions?: Recipe['portions']
  onUpdatePortions?: (numPortions: number) => void
}

export const FlexPortionFooter = ({
  id,
  position = '',
  surchargeLabel,
  isNegativeSurcharge,
  isRecipeAvailable,
  onRecipeDetailsClose,
  numPortions: selectedNumPortions,
  portions,
  onUpdatePortions,
}: FlexPortionFooterProps) => {
  const isFlexRecipesWebEnabled = useFlexRecipesWeb()
  const { getQuantitiesForRecipeId, removeRecipeByUuid } = useBasket()
  const recipeQuantity = getQuantitiesForRecipeId(id)
  const shouldShowFlexTreatment = isFlexRecipesWebEnabled && recipeQuantity > 0

  if (!shouldShowFlexTreatment) {
    return (
      <Box width="100%">
        <AddRecipeButton
          id={id}
          surchargeLabel={surchargeLabel ?? undefined}
          isNegativeSurcharge={isNegativeSurcharge}
          isRecipeAvailable={isRecipeAvailable}
          position={position}
          onRecipeDetailsClose={onRecipeDetailsClose}
        />
      </Box>
    )
  }

  const handleRemoveRecipe = () => {
    removeRecipeByUuid(id, 'detail', position)
  }

  return (
    <ModalFooterContent data-testid="detail-flex-portion-footer">
      <SharedFlexPortionFooter
        recipeCount={recipeQuantity}
        numPortions={selectedNumPortions}
        portions={portions}
        onUpdatePortions={onUpdatePortions ?? (() => undefined)}
        onRemoveRecipe={handleRemoveRecipe}
      />
    </ModalFooterContent>
  )
}
